import { Spacing } from '@persona-id/design-tokens';
import { omit } from 'lodash/fp';
import { memo, MouseEvent as ReactMouseEvent, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import {
  EditorNavItemBase,
  editorNavItemTruncateCSS,
  TruncatedContainer,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem/EditorNavItem';
import InputWithHoverEditButtonField, {
  StyledInput,
  StyledWrapper,
} from 'dashboard/components/FormControls/InputWithHoverEditButtonField';
import { ButtonProps, SuffixIconContainer } from 'design-system/components/Button';
import useToggle from 'lib/hooks/useToggle';

const EDITABLE_TEXT_HEIGHT = 24;
const PUSH_OVERFLOW_PADDING = 30;

// utility to prevent click handler on double click
export const blockDoubleClick = (
  e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  onClick: (event?: ReactMouseEvent) => void,
) => {
  if (e.nativeEvent.detail > 1) return false;
  onClick();
};

const EditableNavItemContainer = styled(EditorNavItemBase)`
  ${SuffixIconContainer} {
    flex-grow: 0;
  }
`;

const EditableTruncatedContainer = styled(TruncatedContainer)<{ hasPrefixIcon?: boolean }>`
  max-height: ${EDITABLE_TEXT_HEIGHT}px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  ${({ hasPrefixIcon }) =>
    hasPrefixIcon &&
    css`
      margin-left: -${PUSH_OVERFLOW_PADDING}px;
    `}
`;

const PreviewText = styled.span`
  font-size: 14px;
  ${editorNavItemTruncateCSS}
`;

const ModifiedInputWithHoverEditField = styled(InputWithHoverEditButtonField)<{
  hasPrefixIcon?: boolean;
}>`
  min-height: ${EDITABLE_TEXT_HEIGHT}px;
  ${({ hasPrefixIcon }) =>
    hasPrefixIcon &&
    css`
      padding-left: ${PUSH_OVERFLOW_PADDING}px;
    `}

  & > div {
    padding: ${parseInt(Spacing[1]) / 2}px 0;
    justify-content: space-between;
  }

  ${StyledWrapper} {
    min-height: ${EDITABLE_TEXT_HEIGHT}px;
    max-height: ${EDITABLE_TEXT_HEIGHT}px;
  }

  ${StyledInput} {
    padding: ${Spacing[1]};
    height: ${EDITABLE_TEXT_HEIGHT}px;
    border-width: 1px;
    box-shadow: none !important; // override
  }
`;

export type EditorRenamebleNavItemProps = ButtonProps & {
  active?: boolean;
  isVisible?: boolean;
  form: string;
  path: string;
  displayPlaceholder?: string;
  editDisabled?: boolean;
};

const EditorRenameableNavItem = (props: EditorRenamebleNavItemProps) => {
  const dispatch = useDispatch();
  const {
    active,
    prefixIcon,
    suffixIcon,
    path,
    title,
    form,
    onClick,
    displayPlaceholder,
    editDisabled,
  } = props;

  const uniqueId = useMemo(() => `${uuid().slice(0, 7)}`, []);
  const hasPrefixIcon = prefixIcon != null;
  const [iconDisplay, { toggle: toggleIconDisplay }] = useToggle(true);

  const InputComponent = (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <ModifiedInputWithHoverEditField
      hasPrefixIcon={hasPrefixIcon}
      size='sm'
      displayElement={<PreviewText />}
      displayPlaceholder={displayPlaceholder ?? null}
      placeholder={title}
      fieldName={uniqueId}
      id={`input-hover-edit-simple-${uniqueId}`}
      onClick={onClick}
      doubleClickToEdit
      onEditButtonClick={toggleIconDisplay}
      onBlur={(value: string) => {
        toggleIconDisplay();
        dispatch(change(form, path, value));
      }}
      disabled={editDisabled}
      active={active}
    />
  );

  return (
    <>
      <EditableNavItemContainer
        editable
        id={`editor-renameable-nav-item-${uniqueId}`}
        {...omit(['children', 'type'], props)}
        suffixIcon={iconDisplay ? suffixIcon : null}
      >
        <EditableTruncatedContainer hasPrefixIcon={hasPrefixIcon}>
          {InputComponent}
        </EditableTruncatedContainer>
      </EditableNavItemContainer>
    </>
  );
};

export default memo(EditorRenameableNavItem);
